import { RadioGroup as RadixRadioGroup } from 'radix-ui';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';

import { cn } from '@/utils/cn';

export const RadioGroup = forwardRef<
  ElementRef<typeof RadixRadioGroup.Root>,
  ComponentPropsWithoutRef<typeof RadixRadioGroup.Root>
>(({ className, ...props }, ref) => (
  <RadixRadioGroup.Root ref={ref} className={cn('grid gap-2', className)} {...props} />
));

RadioGroup.displayName = 'RadioGroup';

export const RadioGroupItem = forwardRef<
  ElementRef<typeof RadixRadioGroup.Item>,
  ComponentPropsWithoutRef<typeof RadixRadioGroup.Item>
>(({ className, ...props }, ref) => (
  <RadixRadioGroup.Item
    ref={ref}
    className={cn(
      'flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full border transition-colors',
      'border-border-strong bg-surface data-[state=checked]:border-brand',
      'disabled:cursor-not-allowed disabled:opacity-50',
      className,
    )}
    {...props}
  >
    <RadixRadioGroup.Indicator className="h-2 w-2 rounded-full bg-brand" />
  </RadixRadioGroup.Item>
));

RadioGroupItem.displayName = 'RadioGroupItem';

/**
 * A radio with its label and optional description as one clickable row.
 *
 * Composed here rather than left to each caller because the label-to-input
 * association is exactly the detail that gets skipped, and a radio you cannot click
 * the words of is a radio that is hard to hit on a phone.
 */
export const RadioGroupOption = ({
  value,
  label,
  description,
  disabled,
  id,
}: {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
  id: string;
}) => (
  <div className="flex items-start gap-2.5">
    <RadioGroupItem value={value} id={id} disabled={disabled} className="mt-0.5" />
    <label htmlFor={id} className={cn('flex-1', disabled ? 'opacity-50' : 'cursor-pointer')}>
      <span className="block text-body text-fg">{label}</span>
      {description ? (
        <span className="block text-caption text-fg-secondary">{description}</span>
      ) : null}
    </label>
  </div>
);
